import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import isNumber from 'lodash.isnumber';
import isString from 'lodash.isstring';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ROUTES, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import {StickyElement} from '@pb/kit-component-sticky-element';

import {NEW_PROFILE_ID} from '../constants';
import {PermissionsTab} from '../permissions';

import {CreateProfileHeadline} from './components/create-profile-headline/create-profile-headline';
import {useNewRoleProfile} from './useNewRoleProfile';

import type {AccessRight} from 'src/hooks/access-rights/constants';

const CreateRoleProfile = () => {
    const navigate = useNavigate();
    const {state} = useLocation();
    const duplicateProfileId = state?.duplicateProfileId;

    const {roleUpdate} = useEventTracking();
    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const [busy, setBusy] = useState(false);

    const {setIsChunkChanged, proceed} = useConfirmationChunk<number>({
        chunkName: 'create-role-profile',
        onProceed: (newProfileId: number | undefined) => {
            if (isNumber(newProfileId)) {
                setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
                navigate(`${ROUTES.PROFILE}/${newProfileId}`);
            }
        },
    });

    const {
        isLoading: isProfileLoading,
        loadingError: profileLoadingError,
        isChanged: isProfileChanged,
        data: profile,
        dataInitial,
        setData: setProfile,
        saveChanges,

        validateProfileName,
        validateDescription,
        validatePermissions,
    } = useNewRoleProfile({duplicateProfileId: duplicateProfileId ? +duplicateProfileId : undefined});

    useEffect(() => {
        setIsChunkChanged(isProfileChanged);
    }, [isProfileChanged, setIsChunkChanged]);

    const saveProfile = useCallback(async () => {
        setBusy(true);
        const response = await saveChanges();
        setBusy(false);

        if (isNumber(response)) {
            proceed(response);
        } else if (isString(response)) {
            setErrorMessage(response);
        }
    }, [saveChanges, proceed, setErrorMessage]);

    const setProfileName = useCallback((profileName: string) => {
        validateProfileName(profileName, dataInitial.profileName);

        setProfile({
            ...profile,
            profileName,
        });

        roleUpdate({
            parameterName: 'Role Name',
            targetRoleId: NEW_PROFILE_ID,
        });
    }, [profile, dataInitial, roleUpdate, setProfile, validateProfileName]);

    const setDescription = useCallback((profileDescription: string) => {
        validateDescription(profileDescription);

        setProfile({
            ...profile,
            profileDescription,
        });

        roleUpdate({
            parameterName: 'Role Description',
            targetRoleId: NEW_PROFILE_ID,
        });
    }, [profile, roleUpdate, setProfile, validateDescription]);

    const setPermissions = useCallback((rightsCodes: string[]) => {
        validatePermissions(rightsCodes);

        roleUpdate({
            parameterName: 'Role Settings',
            targetRoleId: NEW_PROFILE_ID,
        });

        setProfile({
            ...profile,
            rightsCodes,
        });
    }, [profile, roleUpdate, setProfile, validatePermissions]);

    return (
        <ServerResponseHandler
            serverError={profileLoadingError}
            sectionName="Create Role"
            accessRight={ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE as AccessRight}>
            <div>
                <StickyElement>
                    <CreateProfileHeadline
                        isSaveBusy={busy}
                        isLoading={isProfileLoading}
                        isProfileChanged={isProfileChanged}

                        profile={profile}

                        onProfileNameSet={setProfileName}
                        onSave={saveProfile}
                    />
                </StickyElement>
                <PermissionsTab
                    profile={profile}
                    isLoading={isProfileLoading}
                    onSetDescription={setDescription}
                    onSetPermissions={setPermissions}
                />
            </div>
        </ServerResponseHandler>
    );
};

export const ValidatedCreateRoleProfile = () => (
    <ValidationContextProvider>
        <CreateRoleProfile/>
    </ValidationContextProvider>
);
